/**
 * Base Landing Page Configuration
 * 
 * This file contains the default configuration values that all
 * landing page variants inherit from. Override specific values
 * in variant configs to customize each page.
 */

import type { LandingPageConfig } from './types';

export const baseConfig: LandingPageConfig = {
  // Metadata
  id: 'base',
  name: 'Base Template',
  slug: '',
  pageType: 'angle' as const,
  seo: {
    title: 'FluffCo Pillow - Premium Sleep Support',
    description: 'Experience hotel-quality sleep at home with FluffCo pillows. Engineered for consistent support that doesn\'t sag, flatten, or require constant adjustment.',
    keywords: ['pillow', 'sleep', 'hotel quality', 'down alternative', 'neck support'],
  },
  
  // Global settings
  theme: 'light',
  primaryColor: '#007AFF',
  accentColor: '#2d3a5c',
  
  // Section visibility and order
  sections: [
    'hero',
    'benefits',
    'sleeper-types',
    'price-comparison',
    'the-difference',
    'sleep-experts',
    'technology',
    'fluffco-secret',
    'features-grid',
    'testimonials',
    'pricing',
    'faq',
  ],
  
  // Announcement Bar
  announcementBar: {
    text: 'Hair Wellness Month: Save up to 66% on Your Cure',
    showTimer: true,
    ctaText: 'Shop Now →',
    ctaTarget: 'pricing',
  },
  
  // Header
  header: {
    navItems: [
      { id: 'benefits', label: 'Benefits' },
      { id: 'the-difference', label: 'The Difference' },
      { id: 'technology', label: 'Technology' },
      { id: 'testimonials', label: 'Reviews' },
      { id: 'faq', label: 'FAQ' },
    ],
    ctaText: 'Order Now →',
  },
  
  // Hero Section
  hero: {
    headline: 'Clinically Proven Hair Regrowth. Visible in 5 Weeks.',
    subheadline: 'Engineered for consistent support that doesn\'t sag, flatten, or require constant adjustment. Built for sleepers who value reliability over novelty.',
    trustBadge: {
      reviewCount: '546,000+',
      rating: '94/100',
      source: 'The Grooming',
    },
    usps: [
      { icon: 'support', title: 'Consistent Support', description: 'Same feel every night' },
      { icon: 'temperature', title: 'Temperature Neutral', description: 'Breathable cotton cover' },
      { icon: 'hypoallergenic', title: 'Hypoallergenic', description: 'Down alternative fill' },
      { icon: 'location', title: 'Made in USA', description: 'Premium materials' },
    ],
    showOekoTexBadge: true,
    showOprahBadge: true,
    show30NightsBadge: true,
  },
  
  // Gallery - Standard 7-image structure
  // All angle pages should use these base images and only customize overlay text
  gallery: {
    images: [
      { src: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/colagea-box-product_08474614.jpg', alt: 'Colagea Micro-Infuse Treatment Box', type: 'image' },
      { src: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/hair-before-after-bg-X4DwqREq4eXVP5kZcicy8Z.webp', alt: 'Before and After Hair Treatment Results', type: 'image' },
      { src: '', alt: 'Product Arguments', type: 'vial-arguments' },
      { src: '', alt: 'Science Results', type: 'science-results' },
      { src: '', alt: 'What\'s Inside', type: 'ingredients-slide' },
      { src: '', alt: 'Comparison', type: 'comparison' },
      { src: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/serum-application-new-2jWxrnaHfvtDK4hiSW4ndr.webp', alt: 'Serum Application', type: 'image' },
      { src: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/before-after-hair-slide6_60b85227.jpg', alt: 'Before and After Real Results', type: 'image' },
      { src: '', alt: 'Hotel Comparison', type: 'hotel-comparison' },
      { src: '', alt: 'Video Results', type: 'video-slide' },
    ],
    showAwardCarousel: true,
    awardCarouselItems: [
      { title: 'Best Overall Pillow', source: 'Apartment Therapy' },
      { title: 'Best Down Pillow', source: 'Men\'s Health' },
      { title: 'Best Soft Pillow', source: 'Architectural Digest' },
      { title: 'Editor\'s Pick', source: 'Healthline' },
      { title: '94/100 Rating', source: 'The Grooming' },
    ],
  },
  
  // Benefits Section
  benefits: {
    sectionTitle: 'End Your Hair Loss Battle',
    sectionSubtitle: 'Clinically proven micro-infusion technology that targets hair loss at the root. Visible results in just 5 weeks.',
    benefits: [
      { icon: 'moon', title: 'Reactivate Dormant Follicles', description: 'Colagea Micro-Infuse penetrates directly to the root, awakening follicles that topical serums can\'t reach.' },
      { icon: 'refresh', title: 'Visible Density in 5 Weeks', description: 'Clinical studies show 90% of users see measurably thicker hair within 35 days of consistent use.' },
      { icon: 'sparkles', title: 'Clean, Clinical Formula', description: 'No parabens, no sulfates, no silicones. 5 active ingredients backed by peer-reviewed research.' },
    ],
  },
  
  // Hair Types Section (replaces Sleeper Types)
  sleeperTypes: {
    sectionTitle: 'Works for Every Hair Type',
    sectionSubtitle: 'Adapts to Your Needs',
    sleeperTypes: [
      { type: 'back', image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/sleep-position-back_c9753bdc.png', title: 'Thinning Crown', description: 'Targets vertex area' },
      { type: 'side', image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/sleep-position-side_86d85ca5.png', title: 'Receding Hairline', description: 'Strengthens frontal zone' },
      { type: 'stomach', image: 'https://d2xsxph8kpxj0f.cloudfront.net/310519663243023818/kXYViC6LoVarhABtxw9dmA/sleep-position-stomach_ac1d0f29.png', title: 'Overall Thinning', description: 'Full scalp coverage' },
    ],
    trustBadges: [
      { icon: 'heart', text: 'All Hair Types' },
      { icon: 'moon', text: 'Visible Results' },
    ],
  },
  
  // Price Comparison Section
  priceComparison: {
    enabled: true,
    sectionTitle: 'Premium Quality. Without the Premium Markup.',
    sectionSubtitle: 'Traditional retail adds layers of cost that have nothing to do with quality. Distributors, showroom rent, sales commissions. You pay for all of it. We cut them out.',
    retailPrice: 125.90,
    retailBreakdown: [
      { label: 'Mfg', amount: 17.50, color: '#6b7280' },
      { label: 'Distributor', amount: 25.00, color: '#9ca3af' },
      { label: 'Store Rent', amount: 25.00, color: '#d1d5db' },
      { label: 'Staff', amount: 18.40, color: '#e5e7eb' },
      { label: 'Margin', amount: 40.00, color: '#f3f4f6' },
    ],
    directPrice: 41.48,
    directBreakdown: [
      { label: 'Mfg', amount: 17.50, color: '#22c55e' },
      { label: 'Ship', amount: 10.98, color: '#4ade80' },
      { label: 'Us', amount: 13.00, color: '#86efac' },
    ],
    savingsCalculator: {
      show: true,
      quantities: [1, 2, 4],
    },
  },
  
  // The Difference Section
  difference: {
    standardPillowTitle: 'Generic Hair Loss Products',
    standardPillowPoints: [
      'Surface-level application only',
      'Temporary results that fade fast',
      'Harsh chemicals that irritate scalp',
      'No clinical evidence of efficacy',
      'Overpriced for what you get',
    ],
    fluffcoPillowTitle: 'Colagea Micro-Infusion',
    fluffcoPillowPoints: [
      'Micro-needles deliver actives directly to follicles',
      'Clinically proven 90% efficacy rate',
      'Gentle, dermatologist-approved formula',
      'Visible regrowth in just 5 weeks',
      'Patented technology, peer-reviewed results',
    ],
  },
  
  // Sleep Experts Section
  sleepExperts: {
    sectionTitle: 'The Pillow Sleep Experts Recommend',
    sectionSubtitle: '',
    cards: [
      { image: '/images/expert-comfort.png', title: 'Luxury Comfort, Zero Guilt (or Allergies!)', expandedContent: ['Soft, Plush, and Ethically Made', 'Sustainable and 100% Cruelty-Free'] },
      { image: '/images/expert-cool.png', title: 'No More Night Sweats', expandedContent: ['Sleep Like a Baby', 'Built to Last, Night After Night'] },
    ],
  },
  
  // Technology Section
  technology: {
    sectionTitle: 'The Engineering',
    features: [
      { id: 'sanforized', title: 'Sanforized Construction', description: 'A specialized process used by luxury hotel suppliers. Pre-shrinks and stabilizes the fabric, preventing the compression and shape loss that plagues standard pillows.', tags: ['Holds Shape', 'More Durable', 'Easy to Clean', 'Crisp Texture'] },
      { id: 'microfiber', title: 'Microfiber Fill', description: 'Fibers 5x thinner than human hair create plush, down-like support without the allergens. Maintains loft while providing consistent resistance.', tags: ['Hypoallergenic', 'High Loft', 'Temperature Neutral', 'Wear Resistant'] },
    ],
  },
  
  // FluffCo Secret Section
  fluffcoSecret: {
    features: [
      { title: 'Lasting Shape and Comfort', description: '"Sanforizing" is a special and rare way to make pillows that only the 5-star suppliers do. This unique method of craftsmanship allows these pillows to hold their shape night in and night out - as luxury pillows should!', bulletPoints: ['Holds Their Shape', 'More Durable', 'Easy to Clean', 'Extra Crisp Texture', 'Softer to the Touch'] },
      { title: '5x Thinner Than a Hair', description: 'The best pillows are made with the thinnest threads. Hotel-pillows use super thin fibers to make the inside of the pillow feel squishy but also firm at the same time.', bulletPoints: ['Plush, Down-Like Softness', 'Higher Loft (height and fluffiness)', 'Cooler Temperatures', 'Resistant to Wear and Tear', 'Hypoallergenic'] },
    ],
  },
  
  // Features Grid Section
  featuresGrid: {
    sectionTitle: 'Built for the Long Run',
    features: [
      { icon: 'clock', title: 'Holds Shape for Years', description: 'Sanforized construction maintains loft and structure. No flattening, no sagging.' },
      { icon: 'refresh', title: 'No Daily Fluffing', description: 'The structural core holds its position. Make your bed once and forget about it.' },
      { icon: 'droplets', title: 'Machine Washable', description: 'Reinforced seams handle regular cleaning. Proper hygiene without compromise.' },
      { icon: 'shield', title: 'Hypoallergenic', description: 'Down alternative fill provides luxury feel without allergens or ethical concerns.' },
      { icon: 'thermometer', title: 'Temperature Neutral', description: 'Breathable cotton cover stays comfortable. No overheating, no cold spots.' },
      { icon: 'layers', title: 'Consistent Support', description: 'Same feel on night 1,000 as night 1. Your neck knows what to expect.' },
    ],
  },
  
  // Testimonials Section
  testimonials: {
    sectionTitle: '+6,000 5-Star Reviews',
    sectionSubtitle: 'Loved by Hundreds of Thousands',
    testimonials: [
      { name: 'David & Linda', location: 'Chicago, IL', verified: true, content: 'We\'re both in our 60s and have tried EVERYTHING. Memory foam, buckwheat, water pillows... you name it. This is the first one that actually stays put all night. No more 3am pillow fluffing. Finally sleeping through the night again 🙌' },
      { name: 'Margaret T.', location: 'Portland, OR', verified: true, content: 'I can\'t believe I waited so long... After 3 back surgeries, I\'d given up on finding a pillow that actually works. This one just... holds me. I wake up and my neck isn\'t screaming at me anymore. That alone is worth everything 😭' },
      { name: 'Robert K.', location: 'Austin, TX', verified: true, content: '8 months now. Still perfect. I\'ve washed it probably 15 times and it comes out exactly the same every single time. My wife stole mine so I had to order 2 more lol' },
      { name: 'Susan C.', location: 'Seattle, WA', verified: true, content: 'My physical therapist actually asked what I changed!! Said my neck alignment improved dramatically. I almost cried in her office... years of pain and THIS is what finally helped. Worth every penny and then some ❤️' },
      { name: 'James M.', location: 'Boston, MA', verified: true, content: 'After shoulder surgery, I couldn\'t find anything that didn\'t make me wake up in pain. This pillow has been a game changer. My surgeon even commented on how well I\'m healing. Best investment in my recovery!' },
      { name: 'Patricia L.', location: 'Denver, CO', verified: true, content: 'My husband and I both got one after our hotel stay. We couldn\'t believe how well we slept. Now we have them at home and it\'s like sleeping in a 5-star hotel every night. Worth every single cent!' },
      { name: 'Michael R.', location: 'Phoenix, AZ', verified: true, content: 'I work night shifts and sleep during the day. This pillow blocks out everything and keeps me comfortable no matter what position I end up in. Finally getting the rest I need to function. Life changing!' },
      { name: 'Jennifer K.', location: 'Miami, FL', verified: true, content: 'Chronic migraines for 15 years. Tried every pillow imaginable. This one actually supports my neck properly and I\'ve noticed a real difference in my morning headaches. Wish I\'d found this years ago!' },
    ],
    showMoreButton: true,
  },
  
  // Pricing Section
  pricing: {
    sectionTitle: 'Choose Your Setup',
    sectionSubtitle: 'Online-only pricing. Free shipping on all orders.',
    sizes: [
      { id: 'standard', name: 'Standard', dimensions: '20"×28' },
      { id: 'king', name: 'King', dimensions: '20"×36', badge: 'Chosen by 67%' },
    ],
    tiers: {
      standard: [
        { quantity: 1, pricePerPillow: 69.90, totalPrice: 69.90, originalPrice: 125.90, savings: 56.00, savingsPercent: 44, },
        { quantity: 2, pricePerPillow: 54.95, totalPrice: 109.90, originalPrice: 251.80, savings: 141.90, savingsPercent: 56, badge: 'popular' },
        { quantity: 4, pricePerPillow: 41.48, totalPrice: 165.90, originalPrice: 489.90, savings: 324.00, savingsPercent: 66, badge: 'best-value', bonus: { text: '+ 2 Free Pillowcases', value: 116 } },
      ],
      king: [
        { quantity: 1, pricePerPillow: 79.90, totalPrice: 79.90, originalPrice: 135.90, savings: 56.00, savingsPercent: 41, },
        { quantity: 2, pricePerPillow: 64.95, totalPrice: 129.90, originalPrice: 271.80, savings: 141.90, savingsPercent: 52, badge: 'popular' },
        { quantity: 4, pricePerPillow: 46.48, totalPrice: 185.90, originalPrice: 549.90, savings: 364.00, savingsPercent: 66, badge: 'best-value', bonus: { text: '+ 2 Free Pillowcases', value: 136 } },
      ],
    },
    trustBadges: ['30-Night Guarantee', 'Free Shipping', 'Made in USA'],
    pressQuotes: [
      { quote: 'Best Soft Pillow', source: 'Architectural Digest' },
      { quote: 'Best Overall Pillow', source: 'Apartment Therapy' },
      { quote: 'Best Down Pillow', source: 'Men\'s Health' },
      { quote: 'Editor\'s Pick', source: 'Healthline' },
      { quote: '94/100', source: 'The Grooming' },
    ],
  },
  
  // FAQ Section
  faq: {
    sectionTitle: 'Common Questions',
    items: [
      { question: 'What makes FluffCo Pillow so special?', answer: 'FluffCo pillows use Sanforized construction, a specialized process used by luxury hotel suppliers. This pre-shrinks and stabilizes the fabric, preventing the compression and shape loss that plagues standard pillows. Combined with microfiber fill that\'s 5x thinner than human hair, you get consistent support that lasts for years.' },
      { question: 'How are FluffCo Pillows more affordable than hotel brand pillows?', answer: 'We sell directly to you online, cutting out distributors, retail markups, and showroom costs. The same hotel-grade construction that costs significantly more in stores costs a fraction of that when you buy direct from us.' },
      { question: 'Can these pillows help improve my sleep quality?', answer: 'Many customers report significant improvements in sleep quality, particularly those who previously struggled with neck pain or constantly adjusting their pillows. The consistent support helps maintain proper spinal alignment throughout the night.' },
      { question: 'How do I clean and maintain my pillow?', answer: 'FluffCo pillows are fully machine washable. Use a gentle cycle with mild detergent and tumble dry on low. The Sanforized construction ensures the pillow maintains its shape even after multiple washes.' },
      { question: 'Are these pillows ethically and sustainably made?', answer: 'Yes! Our pillows are OEKO-TEX Standard 100 certified, meaning they\'ve been tested for harmful substances. We use down alternative fill, making them 100% cruelty-free and suitable for those with allergies.' },
      { question: 'What is your Good Sleep Guarantee?', answer: 'We offer a 30-Night Better Sleep guarantee. If you\'re not sleeping better within 30 nights, contact us for a full refund. We want you to love your sleep.' },
      { question: 'How long until I receive my order?', answer: 'Orders typically ship within 1-2 business days. Standard delivery takes 2-4 business days within the continental US. Free shipping is included on all orders.' },
    ],
  },
  
  // Footer
  footer: {
    tagline: 'Consistent support. Night after night.',
    links: [
      { label: 'Terms & Conditions', href: '#' },
      { label: 'Terms of Service', href: '#' },
      { label: 'Return Policy', href: '#' },
      { label: 'Privacy Policy', href: '#' },
    ],
    copyright: '© 2026 FluffCo. All rights reserved.',
  },
};

export default baseConfig;
